import { useCallback, useEffect, useState } from "react";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import RefreshIcon from "@mui/icons-material/Refresh";
import SendOutlinedIcon from "@mui/icons-material/SendOutlined";
import baseUrl from "../../../api/baseUrl";
import WhatsAppTemplateCard, { type WhatsAppTemplateRow } from "./WhatsAppTemplateCard";
import { notifyWhatsApp } from "./EmailNotifications";
import { PanelCard, SectionHeader } from "../components/accountUi";

type WhatsAppLogRow = {
  _id: string;
  type: string;
  recipient?: string;
  status: "success" | "failed" | "skipped";
  providerStatus?: string;
  errorCode?: string;
  errorMessage?: string;
  sentAt: string;
  refunded?: boolean;
};

type WhatsAppOverview = {
  platform: {
    configured: boolean;
    sandbox: boolean;
    freeformAllowed: boolean;
    statusCallback: boolean;
    sender: string | null;
    error: string | null;
  };
  sender: {
    own: boolean;
    name: string;
    number: string | null;
  };
  settings: {
    enabled: boolean;
    credits: number;
    lowBalance: boolean;
    noCreditsMessage?: string;
  };
  usage: {
    last7Days: number;
    last30Days: number;
    lastSentAt: string | null;
  };
  templates: WhatsAppTemplateRow[];
  recent: WhatsAppLogRow[];
};

function formatDateTime(value: string | null | undefined) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function errorMessageFrom(error: any, fallback: string) {
  return error?.response?.data?.message || fallback;
}

const STATUS_STYLES: Record<string, string> = {
  success:
    "bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  failed: "bg-rose-50 text-rose-700 dark:bg-rose-950/40 dark:text-rose-300",
  skipped: "bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300",
};

export default function WhatsAppSettingsPanel({ onChanged }: { onChanged?: () => void } = {}) {
  const [overview, setOverview] = useState<WhatsAppOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [testNumber, setTestNumber] = useState("");
  const [testTemplate, setTestTemplate] = useState("");
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ ok: boolean; message: string } | null>(null);

  const loadOverview = useCallback(() => {
    setLoading(true);
    setError(null);
    return baseUrl
      .get("/client/account/whatsapp-overview")
      .then((res) => {
        if (res?.data?.success) setOverview(res.data.data);
        else setError("Could not load WhatsApp details right now.");
      })
      .catch(() => setError("Could not load WhatsApp details right now."))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadOverview();
  }, [loadOverview]);

  const sendTest = () => {
    setTesting(true);
    setTestResult(null);
    baseUrl
      .post("/client/account/whatsapp-test", { to: testNumber, template: selectedTestTemplate })
      .then((res) => {
        setTestResult({ ok: true, message: res?.data?.message || "Test message sent" });
        notifyWhatsApp("success", "WhatsApp message sent!", `Your message was sent to ${testNumber.trim()}.`);
      })
      .catch((err) => {
        const errorMessage = errorMessageFrom(err, "Test message failed");
        setTestResult({ ok: false, message: errorMessage });
        notifyWhatsApp("error", "WhatsApp message not sent", errorMessage);
      })
      .finally(() => {
        setTesting(false);
        loadOverview();
        onChanged?.();
      });
  };

  const platform = overview?.platform;
  const settings = overview?.settings;
  const sender = overview?.sender;
  // Only message types that actually send: a Meta-approved Care32 template,
  // the clinic's own approved wording, or the sandbox.
  const workingTemplates = (overview?.templates ?? []).filter(
    (template) => template.available || template.clinic?.active || platform?.freeformAllowed
  );
  const testTemplates = workingTemplates;
  const noCredits = Boolean(settings) && (settings?.credits ?? 0) <= 0;
  const selectedTestTemplate = testTemplate || testTemplates[0]?.type || "";

  return (
    <div className="space-y-4">
      <PanelCard className="overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
            <SectionHeader
              icon={WhatsAppIcon}
              title="WhatsApp Messages"
              subtitle="Welcome, appointment, and reminder messages on WhatsApp (sent in addition to SMS)"
            />
            <button
              type="button"
              onClick={() => loadOverview()}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 self-start rounded-xl border border-slate-200 bg-white px-4 py-2 text-xs font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
            >
              <RefreshIcon sx={{ fontSize: 16 }} className={loading ? "animate-spin text-primary" : ""} />
              Refresh WhatsApp status
            </button>
          </div>
        </div>

        <div className="space-y-4 p-5">
          {error ? (
            <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
              {error}
            </div>
          ) : null}

          {platform && !platform.configured ? (
            <div className="rounded-xl border border-amber-200/80 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-200">
              {platform.error}
            </div>
          ) : null}

          {platform?.configured && platform.sandbox ? (
            <div className="rounded-xl border border-sky-200/80 bg-sky-50 px-4 py-3 text-sm text-sky-800 dark:border-sky-800/50 dark:bg-sky-950/30 dark:text-sky-200">
              Test mode (Twilio Sandbox). Only phones that have joined the sandbox receive messages.
            </div>
          ) : null}

          <div className="grid gap-3 lg:grid-cols-2">
            <PanelCard className="p-4">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-semibold text-slate-800 dark:text-white">
                    Send WhatsApp messages
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Switched on or off by Care32. Contact Care32 support to change it.
                  </p>
                  <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                    Each message uses 1 WhatsApp token. Failed messages are refunded.
                  </p>
                  {noCredits ? (
                    <p className="mt-2 text-xs text-rose-700 dark:text-rose-300">
                      {settings?.noCreditsMessage ||
                        "WhatsApp tokens are finished. Buy WhatsApp tokens or contact the Care32 team."}
                    </p>
                  ) : settings?.lowBalance ? (
                    <p className="mt-2 text-xs text-amber-700 dark:text-amber-300">
                      WhatsApp tokens are running low. Buy WhatsApp tokens to avoid interruptions.
                    </p>
                  ) : null}
                </div>
                <span
                  className={`shrink-0 rounded-full px-2.5 py-1 text-xs font-semibold ${
                    settings?.enabled
                      ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300"
                      : "bg-slate-100 text-slate-600 dark:bg-slate-700/50 dark:text-slate-300"
                  }`}
                >
                  {settings?.enabled ? "Active" : "Inactive"}
                </span>
              </div>
              <div className="mt-3 rounded-xl bg-slate-50 px-3 py-2 dark:bg-slate-900/40">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  Patients see messages from
                </p>
                {sender?.own ? (
                  <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-white">
                    {sender.name || "Your clinic sender"}
                    {sender.number ? (
                      <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                        ({sender.number})
                      </span>
                    ) : null}
                  </p>
                ) : (
                  <>
                    <p className="mt-0.5 text-sm font-semibold text-slate-800 dark:text-white">
                      Shared Care32 number
                      {sender?.number ? (
                        <span className="ml-1 text-xs font-normal text-slate-500 dark:text-slate-400">
                          ({sender.number})
                        </span>
                      ) : null}
                    </p>
                    <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                      Your clinic name is shown inside each message. To send under your own clinic
                      name, contact Care32 support to set up a WhatsApp sender for your clinic.
                    </p>
                  </>
                )}
              </div>
              <p className="mt-3 text-xs text-slate-500 dark:text-slate-400">
                Last WhatsApp sent: {formatDateTime(overview?.usage.lastSentAt)}
              </p>
            </PanelCard>

            <PanelCard className="p-4">
              <p className="text-sm font-semibold text-slate-800 dark:text-white">Send a test message</p>
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                WhatsApp must be switched on. Uses 1 token.
              </p>
              <select
                value={selectedTestTemplate}
                onChange={(e) => setTestTemplate(e.target.value)}
                aria-label="Template"
                className="mt-3 w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
              >
                {testTemplates.length === 0 ? <option value="">No templates set up</option> : null}
                {testTemplates.map((template) => (
                  <option key={template.type} value={template.type}>
                    {template.label}
                  </option>
                ))}
              </select>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row">
                <input
                  type="tel"
                  value={testNumber}
                  onChange={(e) => setTestNumber(e.target.value)}
                  placeholder="0771234567"
                  className="min-w-0 flex-1 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-800 outline-none focus:border-primary dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                />
                <button
                  type="button"
                  onClick={sendTest}
                  disabled={testing || !testNumber.trim() || !settings?.enabled || !selectedTestTemplate || noCredits}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-primary px-4 py-2 text-xs font-semibold text-white shadow-sm transition hover:opacity-90 disabled:opacity-50"
                >
                  <SendOutlinedIcon sx={{ fontSize: 16 }} />
                  {testing ? "Sending…" : "Send"}
                </button>
              </div>
              {testResult ? (
                <p
                  className={`mt-2 text-xs ${
                    testResult.ok
                      ? "text-emerald-700 dark:text-emerald-300"
                      : "text-rose-700 dark:text-rose-300"
                  }`}
                >
                  {testResult.message}
                </p>
              ) : null}
            </PanelCard>
          </div>
        </div>
      </PanelCard>

      <PanelCard className="overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
          <SectionHeader
            icon={WhatsAppIcon}
            title="WhatsApp Templates"
            subtitle="Edit the wording like your SMS templates. New wording is sent to WhatsApp for approval and used once approved."
          />
        </div>
        <div className="grid gap-3 p-5 lg:grid-cols-2">
          {workingTemplates.map((template) => (
            <WhatsAppTemplateCard
              key={template.type}
              template={template}
              freeformAllowed={Boolean(platform?.freeformAllowed)}
              onChanged={() => loadOverview()}
            />
          ))}
        </div>
      </PanelCard>

      <PanelCard className="overflow-hidden">
        <div className="border-b border-slate-100 px-5 py-4 dark:border-slate-700/80">
          <SectionHeader icon={WhatsAppIcon} title="Recent WhatsApp Messages" subtitle="Last 20 attempts" />
        </div>
        <div className="overflow-x-auto p-5">
          {(overview?.recent ?? []).length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400">No WhatsApp messages yet.</p>
          ) : (
            <table className="w-full min-w-[560px] text-left text-xs">
              <thead className="text-slate-500 dark:text-slate-400">
                <tr>
                  <th className="pb-2 font-semibold">Time</th>
                  <th className="pb-2 font-semibold">Type</th>
                  <th className="pb-2 font-semibold">To</th>
                  <th className="pb-2 font-semibold">Status</th>
                  <th className="pb-2 font-semibold">Details</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-700 dark:divide-slate-700/60 dark:text-slate-200">
                {overview!.recent.map((row) => (
                  <tr key={row._id}>
                    <td className="py-2 pr-3">{formatDateTime(row.sentAt)}</td>
                    <td className="py-2 pr-3 capitalize">{row.type.replace("-", " ")}</td>
                    <td className="py-2 pr-3">{row.recipient || "—"}</td>
                    <td className="py-2 pr-3">
                      <span className={`rounded-full px-2 py-0.5 font-semibold ${STATUS_STYLES[row.status] || ""}`}>
                        {row.status === "success" ? row.providerStatus || "sent" : row.status}
                      </span>
                    </td>
                    <td className="py-2 text-slate-500 dark:text-slate-400">
                      {row.errorMessage || "—"}
                      {row.errorCode ? ` (${row.errorCode})` : ""}
                      {row.refunded ? " · token refunded" : ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </PanelCard>
    </div>
  );
}
